/**
 * Decode the small set of HTML entities that show up in CKEditor heading text
 * so they don't leak into generated anchor slugs.
 */
const decodeEntities = (str) =>
  str
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;|&rsquo;/gi, "'")

/**
 * Turn heading text (possibly containing nested markup/entities) into a clean
 * URL-friendly slug for use as an anchor id.
 */
const slugify = (text) =>
  decodeEntities(String(text).replace(/<[^>]+>/g, ''))
    .toLowerCase()
    .replace(/['’]/g, '') // drop apostrophes rather than hyphenating them
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

/**
 * Inject slugified `id` attributes into every <h2>/<h3> in a blog body so the
 * page exposes deep-linkable `#section` anchors. Headings that already have an
 * id are left untouched, and colliding slugs are deduped with -2, -3, … .
 *
 * The server sanitizes Strapi HTML before calling this; this only adds ids
 * derived from heading text and never interpolates raw text into attributes.
 *
 * @param {string} html - blog body HTML
 * @returns {string} the body HTML with anchor ids added to h2/h3 headings
 */
export const addHeadingAnchors = (html) => {
  if (!html) {
    return html
  }

  // Seed the dedupe map with ids already present in the HTML so a generated
  // slug can never collide with a hand-authored id elsewhere in the document.
  const used = Object.create(null)
  const existingIds = html.match(/\sid\s*=\s*["']([^"']+)["']/gi) || []
  existingIds.forEach((attr) => {
    const value = attr.replace(/^[^"']*["']|["'][^"']*$/g, '')
    if (value) {
      used[value] = 1
    }
  })

  return html.replace(
    /<(h[23])([^>]*)>([\s\S]*?)<\/\1>/gi,
    (match, tag, attrs, inner) => {
      if (/\sid\s*=/i.test(attrs)) {
        return match
      }

      let slug = slugify(inner)
      if (!slug) {
        return match
      }

      if (used[slug]) {
        used[slug] += 1
        slug = `${slug}-${used[slug]}`
      } else {
        used[slug] = 1
      }

      return `<${tag} id="${slug}"${attrs}>${inner}</${tag}>`
    }
  )
}

const NAV_LABEL_MAX_LENGTH = 40

const truncateNavLabel = (text) => {
  if (text.length <= NAV_LABEL_MAX_LENGTH) {
    return text
  }
  const cut = text.slice(0, NAV_LABEL_MAX_LENGTH)
  const lastSpace = cut.lastIndexOf(' ')
  const base = lastSpace > 0 ? cut.slice(0, lastSpace) : cut
  return `${base.replace(/[\s:;–—-]+$/, '')}…`
}

/**
 * Build the in-page "Jump to" nav entries for a blog body that has already
 * been through `addHeadingAnchors`.
 *
 * Rules: every <h2> with an id becomes one entry, in document order, labelled
 * with its plain text. Labels longer than 40 characters are cut at the last
 * word boundary and end in "…"; `text` always keeps the full heading.
 *
 * @param {string} html - anchored blog body HTML
 * @returns {Array<{ id: string, label: string, text: string }>}
 */
export const getBlogNavEntries = (html) => {
  if (!html) {
    return []
  }

  return [...html.matchAll(/<h2\b([^>]*)>([\s\S]*?)<\/h2>/gi)]
    .map(([, attrs, inner]) => {
      const id = attrs.match(/\sid\s*=\s*["']([^"']+)["']/i)?.[1]
      const text = decodeEntities(inner.replace(/<[^>]+>/g, ''))
        .replace(/\s+/g, ' ')
        .trim()
      return { id, text }
    })
    .filter(({ id, text }) => id && text)
    .map(({ id, text }) => ({ id, label: truncateNavLabel(text), text }))
}

/**
 * Add native lazy-loading and async decoding to every <img> in a blog body so
 * off-screen article images (which come straight from CKEditor as raw <img>
 * tags and bypass next/image) don't all download eagerly on first paint.
 * Images that already declare a `loading` attribute are left untouched.
 *
 * The server sanitizes Strapi HTML before calling this; this only adds constants.
 *
 * @param {string} html - blog body HTML
 * @returns {string} the body HTML with loading/decoding hints on images
 */
export const lazyLoadBodyImages = (html) => {
  if (!html) {
    return html
  }

  return html.replace(/<img\b((?:[^>]*?))>/gi, (match, attrs) => {
    if (/\bloading\s*=/i.test(attrs)) {
      return match
    }
    return `<img loading="lazy" decoding="async"${attrs}>`
  })
}
